import type { ContractReconciliationRow } from './sales-force-contract-reconciliation';
import { contractIssueMessages, contractValue } from './contract-reconciliation-presentation';
import type { ContractText } from './contract-reconciliation-presentation';
import { useReconciliationSyncControls } from './reconciliation-sync-controls';

const supportedFields = ['nextRenewalDate', 'terminationDate', 'contractResponsibleEmail', 'currency'];
const renewalWarnings = ['SalesforceRenewalDatePassed', 'WebkuaRenewalDatePassed'];

function restoresContractActivity(row: ContractReconciliationRow, comparedAt: string): boolean {
  const sf = row.salesforceContracts[0];
  const wk = row.webkuaContracts[0];
  return sf?.isCurrent === true && wk?.isCurrent === false
    && row.differences.some((difference) => difference.field === 'terminationDate')
    && sf.terminationDate != null && sf.terminationDate.slice(0, 10) > comparedAt.slice(0, 10)
    && (wk.maintenanceStopDate == null || wk.maintenanceStopDate.slice(0, 10) > comparedAt.slice(0, 10));
}

export function contractSyncBlockReason(row: ContractReconciliationRow, comparedAt: string): string | null {
  if (row.salesforceContracts.length !== 1 || row.webkuaContracts.length !== 1 || !row.salesforceContractNumber?.trim())
    return 'AmbiguousLink';
  const sf = row.salesforceContracts[0];
  const wk = row.webkuaContracts[0];
  if (!sf.id?.trim() || !wk.id?.trim() || !sf.accountId?.trim() || !sf.companyNumber?.trim()
    || sf.companyNumber !== wk.companyNumber
    || sf.salesforceContractNumber !== row.salesforceContractNumber || wk.salesforceContractNumber !== row.salesforceContractNumber)
    return 'AmbiguousLink';
  if (row.issues.length > 0 || row.sourceDiagnostics?.some((diagnostic) => diagnostic.issues.length > 0)
    || row.warnings.some((warning) => !renewalWarnings.includes(warning)))
    return 'UnsafeSource';
  if (row.differences.length === 0) return 'NoDifferences';
  const restoresActivity = restoresContractActivity(row, comparedAt);
  if (!['FieldMismatch', 'ActivityMismatch'].includes(row.kind) || sf.isCurrent !== true ||
    (wk.isCurrent !== true && !restoresActivity)
    || row.differences.some((difference) =>
      !supportedFields.includes(difference.field) && !(difference.field === 'isCurrent' && restoresActivity)))
    return 'UnsupportedDifference';
  if (row.differences.some((difference) => !difference.salesforceValue?.trim())) return 'EmptySource';
  if (row.differences.some((difference) => difference.field === 'terminationDate')
    && sf.terminationDate && sf.terminationDate.slice(0, 10) <= comparedAt.slice(0, 10))
    return 'UnsupportedDifference';
  return null;
}

export function contractSyncBlockedTooltip(row: ContractReconciliationRow, comparedAt: string, text: ContractText): string {
  const reason = contractSyncBlockReason(row, comparedAt);
  const sf = row.salesforceContracts[0];
  const wk = row.webkuaContracts[0];
  const fields = (names: string[]) => [...new Set(names.map((field) => text(`fields.${field}`)))].join(', ');
  switch (reason) {
    case 'AmbiguousLink': {
      const reasons: string[] = [];
      if (row.salesforceContracts.length === 0) reasons.push(text('notFoundInSalesforce'));
      if (row.webkuaContracts.length === 0) reasons.push(text('notFoundInWebkua'));
      if (row.salesforceContracts.length > 1 || row.webkuaContracts.length > 1)
        reasons.push(text('multipleMatches'));
      if (!row.salesforceContractNumber?.trim() || (sf && sf.salesforceContractNumber !== row.salesforceContractNumber)
        || (wk && wk.salesforceContractNumber !== row.salesforceContractNumber))
        reasons.push(text('sync.linkNumber'));
      if (sf && wk && (!sf.companyNumber?.trim() || sf.companyNumber !== wk.companyNumber))
        reasons.push(text('sync.customerLink'));
      if ((sf && !sf.accountId?.trim()) || (sf && !sf.id?.trim()) || (wk && !wk.id?.trim()))
        reasons.push(text('sync.sourceLink'));
      return reasons.join(' ');
    }
    case 'UnsafeSource':
      return [...contractIssueMessages(row, text),
        ...row.warnings.filter((warning) => !renewalWarnings.includes(warning)).map((warning) => text(`warnings.${warning}`))]
        .join(' ');
    case 'UnsupportedDifference': {
      const reasons: string[] = [];
      const restoresActivity = restoresContractActivity(row, comparedAt);
      const unsupported = row.differences.filter((difference) => !supportedFields.includes(difference.field) &&
        !(difference.field === 'isCurrent' && restoresActivity));
      if (unsupported.length > 0)
        reasons.push(text('sync.unsupportedFields', { fields: fields(unsupported.map((difference) => difference.field)) }));
      if (sf.isCurrent !== true) reasons.push(text('sync.inactiveSource', { system: 'SF' }));
      if (wk.isCurrent !== true && !restoresActivity) reasons.push(text('sync.inactiveSource', { system: 'WK' }));
      if (row.differences.some((difference) => difference.field === 'terminationDate') &&
        sf.terminationDate && sf.terminationDate.slice(0, 10) <= comparedAt.slice(0, 10))
        reasons.push(text('sync.todayEndDate'));
      if (reasons.length === 0) reasons.push(text('sync.unsupportedKind', { kind: text(`kinds.${row.kind}`) }));
      return reasons.join(' ');
    }
    case 'EmptySource':
      return text('sync.emptyFields', { fields: fields(row.differences
        .filter((difference) => !difference.salesforceValue?.trim()).map((difference) => difference.field)) });
    default:
      return text('sync.errors.NoDifferences');
  }
}

export function useContractSyncControls(comparedAt: string, onRefresh?: () => void) {
  return useReconciliationSyncControls<ContractReconciliationRow>({
    entity: 'contracts', namespace: 'salesForceContractReconciliation',
    rowKey: (row) => `${row.salesforceContracts[0]?.id}:${row.webkuaContracts[0]?.id}:${row.salesforceContractNumber}`,
    identityField: 'salesforceContractNumber', identity: (row) => row.salesforceContractNumber,
    target: (row) => ({
      salesforceId: row.salesforceContracts[0]?.id, webkuaId: row.webkuaContracts[0]?.id,
      salesforceContractNumber: row.salesforceContractNumber, companyNumber: row.salesforceContracts[0]?.companyNumber,
    }),
    visible: () => true, blockReason: (row) => contractSyncBlockReason(row, comparedAt),
    blockedTooltip: (row, text) => contractSyncBlockedTooltip(row, comparedAt, text),
    differences: (row) => row.differences,
    usesClears: false, clearedFields: () => [],
    fieldName: (field, text) => text(`fields.${field}`), value: contractValue,
    valueColumns: ['sfValue', 'wkValue'],
  }, onRefresh);
}
